import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Button, Radio, Empty, Spin, Switch, Alert, Tag } from "antd";
// MOMENT
import moment from "moment";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// FLAT BUFFERS
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";

function ControlPanelBankVIP({
	hasFrontDoors,
	hasRearDoors,
	bankFloors,
	liftVip,
	socketMessage,
	clearVipMode,
	onClickClose,
	vipServiceModeStopped,
	server,
}: {
	hasFrontDoors: boolean;
	hasRearDoors: boolean;
	bankFloors: BankFloorInterface[];
	liftVip: any;
	vipServiceModeStopped: boolean;
	socketMessage: SocketMessageInterface;
	clearVipMode: () => void;
	onClickClose: () => void;
	server: ServerInterface;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const defaultTimer = 60;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [doorSelected, setDoorSelected] = useState<"FRONT" | "REAR">("FRONT");
	const [secureCalls, setSecureCalls] = useState<boolean>(false);
	const [spinningFloorNo, setSpinningFloorNo] = useState<number>(0);
	const [serviceModeIndex, setServiceModeIndex] = useState<number>(0);
	const [pickUpFloorIndex, setPickUpFloorIndex] = useState<number>(0);
	const [dropOffFloorIndex, setDropOffFloorIndex] = useState<number>(0);
	const [startPositionName, setStartPositionName] = useState<any>("--");
	const [pickUpFloorName, setPickUpFloorName] = useState<any>("--");
	const [dropOffFloorName, setDropOffFloorName] = useState<any>("--");
	const [carIsOnPosition, setCarIsOnPosition] = useState<boolean>(true);
	const [vipState, setVipState] = useState<"SERVICE_MODE" | "PICK_UP" | "DROP_OFF" | "COMPLETE">("SERVICE_MODE");
	const [showFinalAlert, setShowFinalAlert] = useState<boolean>(false);
	const [timeLeft, setTimeLeft] = useState(defaultTimer);
	const [isRunning, setIsRunning] = useState(false);
	const [vipServiceInterrupted, setVipServiceInterrupted] = useState<boolean>(false);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { openModalControlPanelBank } = useSelector((state: RootState) => state.monitoring);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickHallCallVIP = async (floor: number) => {
		setCarIsOnPosition(false);
		// POST COMMAND
		triggerPostCommand({
			floor,
			command: ElevatorControl.CarToLobby,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const onClickCarCall = (floor: number) => {
		// POST COMMAND
		triggerPostCommand({
			floor,
			command: ElevatorControl.VIP,
			liftType: "ELEVATOR",
			active: true,
		});
	};
	const sendEmulatorClearCommand = () => {
		triggerPostCommand({
			floor: 1,
			command: ElevatorControl.VIP,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const pauseTimer = () => setIsRunning(false);

	const resetTimer = () => setTimeLeft(defaultTimer);

	const startTimer = () => setIsRunning(true);

	const clearEmulatorVIPMode = () => {
		setCarIsOnPosition(true);
		setPickUpFloorIndex(0);
		setPickUpFloorName("--");
		setDropOffFloorIndex(0);
		setDropOffFloorName("--");
		setServiceModeIndex(0);
		setStartPositionName("--");
		setVipState("SERVICE_MODE");
		setShowFinalAlert(false);
		clearVipMode();
		sendEmulatorClearCommand();
		resetTimer();
		pauseTimer();
	};

	const onClickHallCall = (floor: number, floorName: any) => {
		setCarIsOnPosition(false);
		if (vipState === "SERVICE_MODE") {
			onClickHallCallVIP(floor);
			setStartPositionName(floorName);
			setServiceModeIndex(floor);
			setVipState("PICK_UP");
			resetTimer();
		} else if (vipState === "PICK_UP") {
			onClickCarCall(floor);
			setPickUpFloorName(floorName);
			setPickUpFloorIndex(floor);
			setVipState("DROP_OFF");
			pauseTimer();
			resetTimer();
		} else if (vipState === "DROP_OFF") {
			onClickCarCall(floor);
			setDropOffFloorName(floorName);
			setDropOffFloorIndex(floor);
			setVipState("COMPLETE");
			resetTimer();
			pauseTimer();
		}
	};

	const onChangeSecureCalls = (checked: boolean) => {
		setSecureCalls(checked);
	};

	const getAlertDescription = () => {
		let alertInformation: [string, string, "warning" | "info" | "error" | "success"] = [
			"Select Start Position",
			"Please select the start position.",
			"warning",
		];
		if (vipState === "PICK_UP") {
			if (!liftVip.id) {
				alertInformation = ["Locating", "Locating the lift for assignment", "warning"];
			}
			if (
				liftVip &&
				liftVip.id &&
				socketMessage &&
				socketMessage.position &&
				socketMessage.position !== serviceModeIndex
			) {
				alertInformation = [`${liftVip.name} Assigned`, "Lift moving toward the start position", "warning"];
			}
			if (socketMessage && socketMessage.position && socketMessage.position === serviceModeIndex) {
				alertInformation = [
					`${liftVip.name} is on position ${startPositionName}`,
					"Please select Pick Up call",
					"warning",
				];
			}
		}
		if (vipState === "DROP_OFF") {
			if (
				liftVip &&
				liftVip.id &&
				socketMessage &&
				socketMessage.position &&
				socketMessage.position !== pickUpFloorIndex
			) {
				alertInformation = ["Pick Up floor assigned", "Lift moving toward the pick up position", "warning"];
			}
			if (socketMessage && socketMessage.position && socketMessage.position === pickUpFloorIndex) {
				alertInformation = [
					`${liftVip.name} is on position ${pickUpFloorName}`,
					"Please select Drop Off call",
					"warning",
				];
			}
		}
		if (vipState === "COMPLETE") {
			if (
				liftVip &&
				liftVip.id &&
				socketMessage &&
				socketMessage.position &&
				socketMessage.position !== dropOffFloorIndex
			) {
				alertInformation = ["Drop Off floor assigned", "Lift moving toward the drop off position", "warning"];
			}
		}
		return alertInformation;
	};

	const formatDuration = () => {
		const duration = moment.duration(timeLeft, "seconds");
		return `${duration.minutes()} min ${duration.seconds()} sec`;
	};
	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (socketMessage && socketMessage.position) {
			if (socketMessage.position === serviceModeIndex) {
				setCarIsOnPosition(true);
				startTimer();
				setVipServiceInterrupted(false);
			}
			if (socketMessage.position === pickUpFloorIndex) {
				setCarIsOnPosition(true);
				startTimer();
				setVipServiceInterrupted(false);
			}
			if (socketMessage.position === dropOffFloorIndex) {
				setCarIsOnPosition(true);
				setShowFinalAlert(true);
				sendEmulatorClearCommand();
				setVipServiceInterrupted(false);
			}
		}
	}, [socketMessage]);

	useEffect(() => {
		if (!openModalControlPanelBank) {
			sendEmulatorClearCommand();
			clearEmulatorVIPMode();
		}
	}, [openModalControlPanelBank]);

	useEffect(() => {
		if (!isRunning || timeLeft <= 0) {
			if (timeLeft === 0) {
				clearEmulatorVIPMode();
			}
			return;
		}

		const timer = setInterval(() => {
			setTimeLeft((prev) => prev - 1);
		}, 1000);

		// eslint-disable-next-line consistent-return
		return () => clearInterval(timer);
	}, [timeLeft, isRunning]);

	useEffect(() => {
		if (vipServiceModeStopped && vipState !== "COMPLETE") {
			clearEmulatorVIPMode();
			setVipServiceInterrupted(true);
			setShowFinalAlert(true);
		}
	}, [vipServiceModeStopped]);

	useEffect(() => {
		if (!hasFrontDoors && hasRearDoors) {
			setDoorSelected("REAR");
		} else if (hasFrontDoors && !hasRearDoors) {
			setDoorSelected("FRONT");
		} else {
			setDoorSelected("FRONT");
		}
	}, [hasFrontDoors, hasRearDoors]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div
			className={`monitoringView__controlPanel__${theme}`}
			style={{ minHeight: "400px", height: "auto", padding: "20px" }}
		>
			{showFinalAlert ? (
				<div className={` drawer__box__control__panel__${theme}  `}>
					{vipServiceInterrupted ? (
						<Alert
							message='VIP Service mode interrupted'
							type='error'
							action={
								<Button size='small' type='primary' onClick={clearEmulatorVIPMode}>
									Accept
								</Button>
							}
							closable={false}
						/>
					) : (
						<Alert
							message='VIP Service mode complete'
							type='success'
							action={
								<Button
									size='small'
									type='primary'
									onClick={() => {
										clearEmulatorVIPMode();
										setVipServiceInterrupted(false);
									}}
								>
									Accept
								</Button>
							}
							closable={false}
						/>
					)}
				</div>
			) : (
				<>
					<div>
						{/** STATUS VIP SERVICE */}
						<div className={` drawer__box__control__panel__${theme}`}>
							<Alert
								className='mb-3'
								message={getAlertDescription()[0]}
								description={getAlertDescription()[1]}
								type={getAlertDescription()[2]}
								showIcon
								icon={
									<FontAwesomeIcon
										icon={faLock}
										fontSize={18}
										className='monitoring__modalSettings__icon generalStyles__mrFix'
									/>
								}
							/>
							<div className='generalStyles__spaceBetween'>
								<div>
									Start Position:
									<Button
										className={
											startPositionName !== "--"
												? "monitoringView__controlPanelNumber__active__vip monitoring__modalSettings__button__1"
												: "monitoring__modalSettings__button__1"
										}
										shape='circle'
										disabled
									>
										<span>{startPositionName}</span>
									</Button>
								</div>
								<div className='generalStyles__mlFix25'>
									Pick Up Floor:
									<Button
										className={
											pickUpFloorName !== "--"
												? "monitoringView__controlPanelNumber__active__vip monitoring__modalSettings__button__1"
												: "monitoring__modalSettings__button__1"
										}
										shape='circle'
										disabled
									>
										<span>{pickUpFloorName}</span>
									</Button>
								</div>
								<div className='generalStyles__mlFix25'>
									Drop off Floor:{" "}
									<Button
										className={
											dropOffFloorName !== "--"
												? "monitoringView__controlPanelNumber__active__vip monitoring__modalSettings__button__1"
												: " monitoring__modalSettings__button__1"
										}
										shape='circle'
										disabled
									>
										<span>{dropOffFloorName}</span>
									</Button>
								</div>
							</div>
						</div>
						{/** PANEL / CIRCLE BUTTONS */}
						<div className={`drawer__box__control__panel__${theme} mt-3`}>
							<Spin spinning={!carIsOnPosition}>
								{(hasFrontDoors || hasRearDoors) && (
									<>
										{/** ACTIONS */}
										<div className='generalStyles__spaceBetween'>
											<div className='generalStyles__flex'>
												<Radio.Group
													value={doorSelected}
													onChange={(e) => {
														setDoorSelected(e.target.value);
													}}
												>
													{hasFrontDoors && <Radio value='F'>{t("general.frontDoors")}</Radio>}
													{hasRearDoors && <Radio value='R'>{t("general.rearDoors")}</Radio>}
												</Radio.Group>
											</div>
											{server.org_id === "88bed3b9-28b1-452f-a2bb-f4a29646bbf8" && (
												<div>
													{secureCalls && (
														<FontAwesomeIcon
															icon={faLock}
															fontSize={18}
															className='monitoring__modalSettings__icon generalStyles__mrFix'
														/>
													)}
													<span className='generalStyles__mrFix' style={{ marginTop: "5px" }}>
														{t("general.secureCalls")}
													</span>
													<Switch checked={secureCalls} onChange={onChangeSecureCalls} />
												</div>
											)}
										</div>
									</>
								)}
								<div className='monitoringView__controlPanelPanel mt-3'>
									{hasFrontDoors || hasRearDoors ? (
										<>
											{bankFloors.map((floor: BankFloorInterface) => (
												<div key={floor.id}>
													<Button
														className='monitoring__modalSettings__button__1'
														shape='circle'
														onClick={() => {
															onClickHallCall(
																floor.floor_index,
																floor && floor.name && floor.name !== ""
																	? floor.name.substring(0, 3).toUpperCase()
																	: floor.floor_index
															);
															setSpinningFloorNo(floor.floor_index);
														}}
														disabled={
															(doorSelected === "FRONT" && !floor.front_door) ||
															(doorSelected === "REAR" && !floor.rear_door) ||
															!carIsOnPosition
														}
													>
														{isLoadingPostCommand && spinningFloorNo === floor.floor_index ? (
															<Spin spinning={isLoadingPostCommand && spinningFloorNo === floor.floor_index} />
														) : (
															<span style={{ marginTop: "3px" }}>
																{floor && floor.name && floor.name !== ""
																	? floor.name.substring(0, 3).toUpperCase()
																	: floor.floor_index}
															</span>
														)}
													</Button>{" "}
												</div>
											))}
										</>
									) : (
										<Empty description={<span>{t("general.bankHasNoFrontRearDoors")}</span>} />
									)}
								</div>
							</Spin>
							<div className='generalStyles__flexCenter mt-3'>
								{isRunning && (
									<div>
										<Tag color='blue'>{formatDuration()} </Tag>
									</div>
								)}
							</div>
						</div>
						{/** CANCEL PROCESS */}
						{(vipState === "PICK_UP" || vipState === "DROP_OFF") && (
							<div className='generalStyles__flexCenter mt-3 generalStyles__width100'>
								<Button onClick={onClickClose} className='buttonStyle__25 generalStyles__width100'>
									CANCEL
								</Button>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}

export default ControlPanelBankVIP;
